import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useOne, useList } from "@refinedev/core";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { PageHeader } from "@/components/forms/PageHeader";
import { MoneyDisplay } from "@/components/forms/MoneyDisplay";
import { ConfirmDialog } from "@/components/forms/ConfirmDialog";

interface LiquidacionRow {
  id: string;
  monto_bruto: number;
  monto_fee: number;
  iva_fee: number;
  descuento_4x1000: number;
  monto_neto: number;
  venta_pagos: {
    venta_id: string;
    ventas: { cliente_nombre: string; numero_consecutivo: number };
    financieras: { nombre: string };
  };
}

export function LiquidarPeriodoPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [calculando, setCalculando] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const { data: periodo, refetch: refetchPeriodo } = useOne({
    resource: "periodos_cobro",
    id,
    meta: { select: "*, tiendas(nombre, marca, tipo_producto)" },
  });
  const { data: liquidaciones, refetch: refetchLiq } = useList<LiquidacionRow>({
    resource: "liquidaciones",
    filters: id ? [{ field: "periodo_cobro_id", operator: "eq", value: id }] : [],
    meta: {
      select: "*, venta_pagos(venta_id, ventas(cliente_nombre, numero_consecutivo), financieras(nombre))",
    },
    pagination: { mode: "off" },
  });

  async function calcular() {
    setCalculando(true);
    const { error } = await supabase.rpc("fn_calcular_liquidacion", { p_periodo_id: id });
    setCalculando(false);
    if (error) {
      toast.error("Error", { description: error.message });
      return;
    }
    toast.success("Liquidación calculada");
    await refetchPeriodo();
    await refetchLiq();
  }

  async function confirmar() {
    setConfirmando(true);
    const { error } = await supabase.rpc("fn_confirmar_liquidacion", { p_periodo_id: id });
    setConfirmando(false);
    setShowConfirm(false);
    if (error) {
      toast.error("Error", { description: error.message });
      return;
    }
    toast.success("Liquidación confirmada. Notificada a la tienda.");
    navigate("/admin/kanban");
  }

  if (!periodo?.data) return <div className="card p-8 text-center">Cargando...</div>;
  const p = periodo.data;
  const yaLiquidado = (liquidaciones?.data ?? []).length > 0;

  return (
    <div className="max-w-3xl space-y-4">
      <PageHeader
        title={`Cuenta de cobro #${p.numero_correlativo}`}
        subtitle={`${(p.tiendas as { nombre: string })?.nombre} · ${p.cantidad_ventas} ventas`}
      />

      {!yaLiquidado && p.estado === "cerrado_tienda" && (
        <button onClick={calcular} disabled={calculando} className="btn-primary w-full">
          {calculando ? "Calculando..." : "Calcular liquidación"}
        </button>
      )}

      {yaLiquidado && (
        <>
          <section className="card overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-bg text-fg-secondary">
                <tr>
                  <th className="text-left p-3">Cliente</th>
                  <th className="text-left p-3">Fin.</th>
                  <th className="text-right p-3">Bruto</th>
                  <th className="text-right p-3">4x1k</th>
                  <th className="text-right p-3">Fee</th>
                  <th className="text-right p-3">IVA</th>
                  <th className="text-right p-3">Neto</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {liquidaciones?.data.map((row) => (
                  <tr key={row.id}>
                    <td className="p-3">
                      #{row.venta_pagos.ventas.numero_consecutivo}{" "}
                      {row.venta_pagos.ventas.cliente_nombre}
                    </td>
                    <td className="p-3">{row.venta_pagos.financieras.nombre}</td>
                    <td className="p-3 text-right money">
                      <MoneyDisplay value={row.monto_bruto} />
                    </td>
                    <td className="p-3 text-right money text-fg-muted">
                      <MoneyDisplay value={row.descuento_4x1000} />
                    </td>
                    <td className="p-3 text-right money text-fg-muted">
                      <MoneyDisplay value={row.monto_fee} />
                    </td>
                    <td className="p-3 text-right money text-fg-muted">
                      <MoneyDisplay value={row.iva_fee} />
                    </td>
                    <td className="p-3 text-right money font-medium">
                      <MoneyDisplay value={row.monto_neto} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="card p-4 space-y-2">
            <h2 className="font-semibold">Resumen</h2>
            <ResumenLinea label="Total bruto financieras" value={p.total_bruto_financieras} />
            <ResumenLinea label="− 4x1000" value={-p.total_4x1000} />
            <ResumenLinea label="− Fees" value={-p.total_fees} />
            <ResumenLinea label="− IVA fees" value={-p.total_iva_fees} />
            <hr className="border-border" />
            <ResumenLinea label="− Ajustes por anulaciones" value={-p.total_ajustes_anulaciones} />
            <hr className="border-border" />
            <div className="flex items-center justify-between text-lg">
              <span className="font-semibold">A transferir</span>
              <MoneyDisplay value={p.valor_final_transferencia} className="font-bold text-brand" />
            </div>
          </section>

          {p.estado === "cerrado_tienda" && (
            <div className="flex gap-2">
              <button onClick={calcular} className="btn-secondary">Recalcular</button>
              <button onClick={() => setShowConfirm(true)} className="btn-primary flex-1">
                Confirmar y notificar a tienda
              </button>
            </div>
          )}

          {p.estado === "liquidado" && (
            <button
              onClick={() => navigate(`/pagos/nuevo?periodo_id=${id}`)}
              className="btn-primary w-full"
            >
              Registrar pago
            </button>
          )}
        </>
      )}

      <ConfirmDialog
        open={showConfirm}
        title="¿Confirmar liquidación?"
        description="Se notificará a la tienda y los porcentajes aplicados quedan congelados."
        confirmLabel={confirmando ? "Confirmando..." : "Sí, confirmar"}
        onConfirm={confirmar}
        onCancel={() => setShowConfirm(false)}
      />
    </div>
  );
}

function ResumenLinea({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-fg-secondary">{label}</span>
      <MoneyDisplay value={value} />
    </div>
  );
}
